import React, { useState, useEffect, useMemo, useRef } from "react";
import { styled } from "@glitz/react";
import { useParams, useNavigate } from "react-router-dom";
import { GrFormPrevious, GrFormNext, GrFilter } from "react-icons/gr";
import { applyClassName } from "@glitz/react";
import { Product } from "../types";
import { spacing, Size, Theme, green, white, grey, lightGrey, fontSize, fontWeight } from "../theme";
import { useViewport } from "../hooks/useViewport";
import ProductCard from "../components/ProductCard";
import Select from "../components/basic/Select";
import Button from "../components/basic/Button";
import Loader from "../components/basic/Loader";
import { BreadCrumbs } from "../components/BreadCrumbs";
import { Category } from "../types";
import CloseIcon from "../components/CloseIcon";

type SubCategory = Category;

const SCROLL_STEP = 200;

const CategoryPage: React.FC = () => {
  const { main, sub } = useParams<{ main: string; sub?: string }>();
  const { isMobile } = useViewport();
  const [products, setProducts] = useState<Product[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [sort, setSort] = useState("name");
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFilterOpen, setFilterOpen] = useState(false);
  const [isSortOpen, setSortOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    setHasOverflow(overflow);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    el.addEventListener("scroll", updateScrollState);
    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", updateScrollState);
    };
  }, [subCategories]);

  const scrollSubCategories = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === "left" ? -SCROLL_STEP : SCROLL_STEP, behavior: "smooth" });
  };

  const handleCategoryClick = (slug: string) => {
    navigate(`/kategorier/${main}/${slug}`);
    setFilterOpen(false);
  };

  useEffect(() => {
    setProducts([]);
    setLoading(true);

    const fetchProducts = async () => {
      try {
        if (sub) {
          const res = await fetch(`/api/categories/${sub}/products`);
          const data = await res.json();
          setProducts(data);
        } else if (main) {
          const res = await fetch(`/api/categories/${main}/products`);
          const data = await res.json();
          setProducts(data);
        }
      } catch (err) {
        console.error("Failed to fetch products:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [main, sub]);

  useEffect(() => {
    setSubCategories([]);
    const fetchSubCategories = async () => {
      if (!main) return;
      try {
        const res = await fetch(`/api/product-subcategories/${main}`);
        const data = await res.json();
        setSubCategories(data);
      } catch (err) {
        console.error("Failed to fetch subcategories:", err);
      }
    };

    fetchSubCategories();
  }, [main]);

  const capitalizeFirstLetter = (text: string) => {
    if (!text) return "";
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Visningsnamn för aktiv subkategori: hämta name från Woo-kategorin
  // (URL:en innehåller bara slugen, t.ex. "under-aktivitet").
  const subName = useMemo(() => {
    if (!sub) return undefined;
    const match = subCategories.find((c) => c.slug === sub);
    return match?.name ?? capitalizeFirstLetter(sub.replace(/-/g, " "));
  }, [sub, subCategories]);

  const categoryHeader = () => {
    if (subName) return capitalizeFirstLetter(subName);
    return capitalizeFirstLetter(main ?? "");
  };

  const sortOptions = [
    { value: "name", label: "Namn (A–Ö)" },
    { value: "priceLow", label: "Lägsta pris" },
    { value: "priceHigh", label: "Högsta pris" },
  ];

  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sort) {
      case "priceLow":
        return sorted.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      case "priceHigh":
        return sorted.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
      case "name":
      default:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [sort, products]);

  return (
    <PageWrapper className="category-page">
      <BreadCrumbs main={main ?? ""} sub={subName} />
      <TopWrapper className="category-top">
        <CategoryHeader>{categoryHeader()}</CategoryHeader>
        {isMobile ? (
          <MobileTopRight>
            <FilterButton type="button" className="category-filter-btn" onClick={() => setFilterOpen(true)}>
              <FilterIcon />
              Filter
            </FilterButton>
            <SortButton type="button" className="category-filter-btn" onClick={() => setSortOpen(true)}>
              {sortOptions.find((o) => o.value === sort)?.label ?? "Sortera"}
            </SortButton>
          </MobileTopRight>
        ) : (
          <>
            <SubCategoryStrip className="category-subcategory-strip">
              {hasOverflow && (
                <ScrollBtn
                  type="button"
                  aria-label="Scrolla vänster"
                  onClick={() => scrollSubCategories("left")}
                  disabled={!canScrollLeft}
                >
                  <GrFormPrevious color={green} />
                </ScrollBtn>
              )}
              <ScrollContainer>
                <SubCategoryScroll ref={scrollRef}>
                  {subCategories?.length > 0 &&
                    subCategories.map((subCategory) => (
                      <SubCategoryButton
                        size={Size.Small}
                        as={Theme.Dark}
                        ghost={subCategory.slug !== sub}
                        key={subCategory.id}
                        onClick={() => handleCategoryClick(subCategory.slug)}
                      >
                        {subCategory.name}
                      </SubCategoryButton>
                    ))}
                </SubCategoryScroll>
                {canScrollRight && <FadeRight />}
              </ScrollContainer>
              {hasOverflow && (
                <ScrollBtn
                  type="button"
                  aria-label="Scrolla höger"
                  onClick={() => scrollSubCategories("right")}
                  disabled={!canScrollRight}
                >
                  <GrFormNext color={green} />
                </ScrollBtn>
              )}
            </SubCategoryStrip>
            <div className="category-sort">
              <SortSelect
                onChange={(val) => setSort(val)}
                options={sortOptions}
                label="Sortera"
                value={sort}
              />
            </div>
          </>
        )}
      </TopWrapper>
      <ProductWrapper className="category-product-wrapper">
        {loading && <Loader ariaLabel="Laddar produkter" />}
        {!loading && sortedProducts?.length > 0 && sortedProducts?.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
        {!loading && sortedProducts?.length === 0 && <div>Inga produkter hittades</div>}
      </ProductWrapper>

      {isMobile && (
        <>
          {isFilterOpen && <FilterOverlay onClick={() => setFilterOpen(false)} />}
          <FilterFlyout className={isFilterOpen ? "category-filter-open" : "category-filter-closed"}>
            <FilterFlyoutTop>
              <FilterFlyoutTitle>Underkategorier</FilterFlyoutTitle>
              <CloseIcon onClose={() => setFilterOpen(false)} />
            </FilterFlyoutTop>
            <FilterFlyoutList>
              {subCategories.length > 0 ? (
                subCategories.map((subCategory) => (
                  <FilterFlyoutItem
                    key={subCategory.id}
                    onClick={() => handleCategoryClick(subCategory.slug)}
                  >
                    {subCategory.name}
                  </FilterFlyoutItem>
                ))
              ) : (
                <FilterFlyoutEmpty>Inga underkategorier</FilterFlyoutEmpty>
              )}
            </FilterFlyoutList>
          </FilterFlyout>

          {isSortOpen && <FilterOverlay onClick={() => setSortOpen(false)} />}
          <SortFlyout className={isSortOpen ? "category-filter-open" : "category-filter-closed"}>
            <FilterFlyoutTop>
              <FilterFlyoutTitle>Sortera</FilterFlyoutTitle>
              <CloseIcon onClose={() => setSortOpen(false)} />
            </FilterFlyoutTop>
            <FilterFlyoutList>
              {sortOptions.map((opt) => (
                <FilterFlyoutItem
                  key={opt.value}
                  onClick={() => {
                    setSort(opt.value);
                    setSortOpen(false);
                  }}
                >
                  {opt.label}
                </FilterFlyoutItem>
              ))}
            </FilterFlyoutList>
          </SortFlyout>
        </>
      )}
    </PageWrapper>
  );
};

const PageWrapper = styled.div({
  paddingLeft: spacing.m,
  minHeight: "60vh",
});

const TopWrapper = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: spacing.xl,
});

const CategoryHeader = styled.h1({});

const SubCategoryStrip = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: 0,
  margin: { x: spacing.m },
});

const ScrollContainer = styled.div({
  position: "relative" as const,
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: 0,
  overflow: "hidden",
});

const SubCategoryScroll = styled.div({
  display: "flex",
  gap: spacing.m,
  flexWrap: "nowrap",
  overflowX: "auto",
  overflowY: "hidden",
  scrollBehavior: "smooth",
});

const ScrollBtn = styled.button( {
  flexShrink: 0,
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: { xy: { style: "solid", color: green, width: "1px" } },
  borderRadius: "999px",
  backgroundColor: "white",
  cursor: "pointer",
   fontSize: "20px",
  ":disabled": {
    opacity: 0.4,
    cursor: "default",
  },
  ":hover": {
    backgroundColor: "#f5f5f5",
  },
});

const FadeRight = styled.div({
  position: "absolute" as const,
  right: 0,
  top: 0,
  bottom: 0,
  width: "24px",
  backgroundColor: "transparent",
  backgroundImage: "linear-gradient(to right, transparent, rgba(255,255,255,0.9))",
  pointerEvents: "none",
});

const SubCategoryButton = styled(Button, {
  whiteSpace: "nowrap",
});

const SortSelect = styled(Select, {
  width: "160px",
  minWidth: "140px",
  flexShrink: 0,
  marginRight: spacing.m,
});

const MobileTopRight = styled.div({
  display: "flex",
  alignItems: "stretch",
  gap: spacing.s,
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: 0,
});

const FilterButton = styled.button({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  padding: { x: spacing.m, y: spacing.s },
  border: { xy: { style: "solid", color: green, width: "2px" } },
  borderRadius: "6px",
  backgroundColor: white,
  color: green,
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  cursor: "pointer",
  ":hover": {
    backgroundColor: lightGrey,
  },
});

const FilterIcon = styled(applyClassName(GrFilter), {
  fontSize: "18px",
});

const SortButton = styled.button({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: { x: spacing.m, y: spacing.s },
  border: { xy: { style: "solid", color: green, width: "2px" } },
  borderRadius: "6px",
  backgroundColor: white,
  color: green,
  fontSize: fontSize.m,
  fontWeight: fontWeight.bold,
  cursor: "pointer",
  flex: { grow: 1, shrink: 1, basis: 0 },
  minWidth: 0,
  ":hover": {
    backgroundColor: lightGrey,
  },
});

const SortFlyout = styled.div({
  position: "fixed",
  top: 0,
  right: 0,
  width: "300px",
  maxWidth: "85vw",
  height: "100vh",
  backgroundColor: white,
  zIndex: 1200,
  display: "flex",
  flexDirection: "column",
  boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
  transitionProperty: "transform",
  transitionDuration: "0.3s",
  transitionTimingFunction: "ease-in-out",
});

const FilterOverlay = styled.div({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.4)",
  zIndex: 1199,
});

const FilterFlyout = styled.div({
  position: "fixed",
  top: 0,
  right: 0,
  width: "300px",
  maxWidth: "85vw",
  height: "100vh",
  backgroundColor: white,
  zIndex: 1200,
  display: "flex",
  flexDirection: "column",
  boxShadow: "-4px 0 20px rgba(0,0,0,0.15)",
  transitionProperty: "transform",
  transitionDuration: "0.3s",
  transitionTimingFunction: "ease-in-out",
});

const FilterFlyoutTop = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: { x: spacing.m, y: spacing.s },
  borderBottom: { style: "solid", color: grey, width: "1px" },
});

const FilterFlyoutTitle = styled.h2({
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
});

const FilterFlyoutList = styled.nav({
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  flex: { grow: 1, shrink: 1, basis: 0 },
});

const FilterFlyoutItem = styled.div({
  padding: { x: spacing.l, y: spacing.m },
  cursor: "pointer",
  fontSize: fontSize.m,
  borderBottom: { style: "solid", color: lightGrey, width: "1px" },
  ":hover": {
    backgroundColor: lightGrey,
  },
});

const FilterFlyoutEmpty = styled.div({
  padding: { xy: spacing.l },
  fontSize: fontSize.m,
  color: grey,
});

const ProductWrapper = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: spacing.m,
  marginBottom: spacing.m,
});

export default CategoryPage;
